import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import AppLayout from "../../components/AppLayout";
import { api } from "../../api/client";
import { Building2, Plus, Users, BookOpen, ChevronRight, Loader2, X, Trash2 } from "lucide-react";
import { toast } from "sonner";

const inp = "w-full bg-[#111318] border border-white/10 rounded-xl px-4 py-2.5 text-sm text-white focus:outline-none focus:border-[#CFFF50]/40 placeholder-gray-600";
const lbl = "block text-xs text-gray-400 mb-1.5 font-medium";

export default function AdminOrganisations() {
  const navigate = useNavigate();
  const [orgs,    setOrgs]    = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [confirmDel, setConfirmDel] = useState(null);
  const [form, setForm] = useState({ name: "", seat_limit: 100 });
  const [saving, setSaving] = useState(false);

  const load = () => {
    api.get("/admin/organisations")
      .then(setOrgs)
      .catch(() => toast.error("Failed to load"))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const create = async () => {
    if (!form.name.trim()) return toast.error("Name required");
    setSaving(true);
    try {
      await api.post("/admin/organisations", { name: form.name, seat_limit: parseInt(form.seat_limit) || 100 });
      toast.success("Organisation created");
      setShowCreate(false);
      setForm({ name: "", seat_limit: 100 });
      load();
    } catch { toast.error("Failed to create"); }
    finally { setSaving(false); }
  };

  const del = async () => {
    try {
      await api.del(`/admin/organisations/${confirmDel.id}`);
      toast.success("Deleted");
      setConfirmDel(null);
      load();
    } catch { toast.error("Failed to delete"); }
  };

  const totalLearners = orgs.reduce((a, o) => a + (o.seats_used || 0), 0);
  const totalSeats    = orgs.reduce((a, o) => a + (o.seat_limit || 0), 0);

  return (
    <AppLayout searchPlaceholder="Search organisations...">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tight">Organisations</h1>
          <p className="text-sm text-gray-400 mt-2">
            {orgs.length} organisation{orgs.length !== 1 ? "s" : ""} · {totalLearners} / {totalSeats} seats used
          </p>
        </div>
        <button onClick={() => setShowCreate(true)}
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-lime text-ink-900 text-sm font-bold hover:bg-lime-hover transition-all">
          <Plus className="w-4 h-4" /> New Organisation
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4 mb-8">
        {[
          { label: "Organisations", value: orgs.length, icon: Building2 },
          { label: "Total learners", value: totalLearners, icon: Users },
          { label: "Total seats",    value: totalSeats,    icon: BookOpen },
        ].map(({ label, value, icon: Icon }) => (
          <div key={label} className="bg-white/[0.03] border border-white/[0.08] rounded-2xl p-5">
            <Icon size={15} className="text-[#CFFF50] mb-3" />
            <div className="text-3xl font-black">{value}</div>
            <div className="text-xs text-gray-500 mt-1">{label}</div>
          </div>
        ))}
      </div>

      {/* List */}
      {loading ? (
        <div className="flex items-center justify-center py-20 text-gray-500">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
        </div>
      ) : orgs.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 border border-white/[0.06] rounded-2xl text-center">
          <Building2 className="w-10 h-10 text-gray-700 mb-3" />
          <p className="text-gray-400 font-semibold">No organisations yet</p>
          <p className="text-sm text-gray-600 mt-1">Create your first organisation to manage corporate training.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {orgs.map((org) => {
            const pct = org.seat_limit ? Math.round((org.seats_used / org.seat_limit) * 100) : 0;
            return (
              <div key={org.id}
                className="group bg-white/[0.03] border border-white/[0.08] rounded-2xl p-6 hover:border-white/20 transition-colors cursor-pointer"
                onClick={() => navigate(`/admin/organisations/${org.id}`)}
              >
                {/* Header */}
                <div className="flex items-start justify-between mb-4">
                  <div className="w-12 h-12 rounded-2xl bg-[#7C5CFF]/15 border border-[#7C5CFF]/30 flex items-center justify-center text-lg font-black text-[#7C5CFF]">
                    {org.name.charAt(0).toUpperCase()}
                  </div>
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button onClick={(e) => { e.stopPropagation(); setConfirmDel(org); }}
                      className="p-1.5 rounded-lg text-gray-600 hover:text-red-400 hover:bg-red-500/10 transition-colors">
                      <Trash2 size={13} />
                    </button>
                    <ChevronRight size={16} className="text-gray-600" />
                  </div>
                </div>

                <h3 className="font-bold text-lg leading-tight mb-1">{org.name}</h3>
                <p className="text-xs text-gray-500 mb-4">
                  {(org.managers || []).length} manager{(org.managers || []).length !== 1 ? "s" : ""} ·{" "}
                  {(org.assigned_courses || []).length} course{(org.assigned_courses || []).length !== 1 ? "s" : ""}
                </p>

                {/* Seat usage */}
                <div>
                  <div className="flex justify-between text-xs mb-1.5">
                    <span className="text-gray-500">Seats used</span>
                    <span className="text-gray-300 font-semibold">{org.seats_used} / {org.seat_limit}</span>
                  </div>
                  <div className="h-1.5 rounded-full bg-white/10 overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all ${pct >= 90 ? "bg-red-400" : pct >= 70 ? "bg-yellow-400" : "bg-[#CFFF50]"}`}
                      style={{ width: `${Math.min(pct, 100)}%` }}
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Create modal */}
      {showCreate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-6" onClick={() => setShowCreate(false)}>
          <div className="w-full max-w-md bg-[#111318] border border-white/10 rounded-2xl p-7" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-bold text-xl">New Organisation</h2>
              <button onClick={() => setShowCreate(false)} className="text-gray-600 hover:text-white"><X size={18} /></button>
            </div>
            <div className="space-y-4">
              <div><label className={lbl}>Organisation name *</label><input className={inp} placeholder="Acme Security Ltd." value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} autoFocus /></div>
              <div><label className={lbl}>Seat limit</label><input type="number" min={1} className={inp} value={form.seat_limit} onChange={(e) => setForm({ ...form, seat_limit: e.target.value })} /></div>
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button onClick={() => setShowCreate(false)} className="px-4 py-2 rounded-full border border-white/10 text-sm hover:bg-white/5">Cancel</button>
              <button onClick={create} disabled={saving} className="px-5 py-2 rounded-full bg-lime text-ink-900 text-sm font-bold hover:bg-lime-hover disabled:opacity-50">
                {saving ? "Creating…" : "Create"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm delete */}
      {confirmDel && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-6" onClick={() => setConfirmDel(null)}>
          <div className="w-full max-w-sm bg-[#111318] border border-white/10 rounded-2xl p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-bold text-lg mb-2">Delete organisation?</h3>
            <p className="text-sm text-gray-400">This will remove <strong>{confirmDel.name}</strong> and all its configuration. Learner accounts and enrollments are preserved.</p>
            <div className="mt-5 flex justify-end gap-3">
              <button onClick={() => setConfirmDel(null)} className="px-4 py-2 rounded-full border border-white/10 text-sm">Cancel</button>
              <button onClick={del} className="px-4 py-2 rounded-full bg-red-500 text-white text-sm font-bold">Delete</button>
            </div>
          </div>
        </div>
      )}
    </AppLayout>
  );
}
